import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import '../styles/Leave.css';

const TYPE_LABELS = {
  annual: 'Annual leave',
  sick: 'Sick leave',
  maternity: 'Maternity leave',
  paternity: 'Paternity leave',
};

const LOW_DAYS = 5;

const apiBase = () => `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/leave`;

const money = (amount) => Number(amount || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatDays = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';
  return String(number);
};

const countLabel = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const EmployeeBenefitsDashboard = () => {
  const { hasPermission } = useAuth();
  const canHr = hasPermission('/leave-requests/hr', 'Edit');
  const canEdit = hasPermission('/leave-requests', 'Edit');
  const canOpenCard = hasPermission('/employee-list', 'View');
  const [data, setData] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [nameQuery, setNameQuery] = useState('');
  const autoOpened = useRef(false);

  const load = async () => {
    const response = await axios.get(`${apiBase()}/benefits-dashboard`);
    setData(response.data);
  };

  useEffect(() => {
    load().catch((err) => {
      console.error(err);
      setError('Could not load employee benefits.');
    });
  }, []);

  const payDue = data?.pay_due || [];
  const leaveDays = data?.leave_days || [];
  const comingSoon = data?.coming_soon || [];
  const waitingForHr = data?.waiting_for_hr || [];
  const onLeave = data?.on_leave_today || [];
  const waitingForManager = data?.waiting_for_manager || [];
  const needsSetup = data?.needs_setup || [];
  const endOfService = data?.end_of_service || [];
  const lowDays = leaveDays.filter((item) => {
    const remaining = Number(item.remaining_days);
    const used = Number(item.used_days);
    return remaining <= LOW_DAYS && (used > 0 || remaining > 0);
  });

  useEffect(() => {
    if (!data || autoOpened.current) return;
    autoOpened.current = true;
    if (payDue.length) setOpenId('pay');
    else if (waitingForHr.length) setOpenId('hr');
    else if (needsSetup.length) setOpenId('setup');
  }, [data, payDue.length, waitingForHr.length, needsSetup.length]);

  const act = async (id, action, body) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await axios.post(`${apiBase()}/requests/${id}/${action}`, body || {});
      setMessage('Leave request updated.');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update this request.');
    } finally {
      setBusy(false);
    }
  };

  const headline = useMemo(() => {
    const parts = [];
    if (payDue.length) parts.push(countLabel(payDue.length, 'allowance due', 'allowances due'));
    if (waitingForHr.length) parts.push(countLabel(waitingForHr.length, 'leave request waiting for you', 'leave requests waiting for you'));
    if (needsSetup.length) parts.push(countLabel(needsSetup.length, 'person needs the accountant', 'people need the accountant'));
    if (!parts.length) return 'Nothing needs you today.';
    return `${parts.join('. ')}.`;
  }, [payDue.length, waitingForHr.length, needsSetup.length]);

  const cards = [
    {
      id: 'pay',
      count: payDue.length,
      title: 'Allowance due',
      status: payDue.length ? 'Needs you' : 'None today',
      tone: payDue.length ? 'attention' : 'clear',
    },
    {
      id: 'hr',
      count: waitingForHr.length,
      title: 'Waiting for you',
      status: waitingForHr.length ? 'Needs you' : 'None',
      tone: waitingForHr.length ? 'attention' : 'clear',
    },
    {
      id: 'out',
      count: onLeave.length,
      title: 'On leave today',
      status: onLeave.length ? 'Out today' : 'Everyone is in',
      tone: onLeave.length ? 'watch' : 'clear',
    },
    {
      id: 'soon',
      count: comingSoon.length,
      title: 'Coming soon',
      status: 'Next 30 days',
      tone: comingSoon.length ? 'watch' : 'clear',
    },
    {
      id: 'service',
      count: endOfService.length,
      title: 'End of service this year',
      status: endOfService.length ? 'Set aside' : 'None this year',
      tone: 'clear',
    },
    {
      id: 'manager',
      count: waitingForManager.length,
      title: 'With the manager',
      status: waitingForManager.length ? 'Still waiting' : 'None',
      tone: waitingForManager.length ? 'watch' : 'clear',
    },
    {
      id: 'setup',
      count: needsSetup.length,
      title: 'Needs setup',
      status: needsSetup.length ? 'Ask the accountant' : 'All set up',
      tone: needsSetup.length ? 'attention' : 'clear',
    },
    {
      id: 'days',
      count: lowDays.length,
      title: 'Days running low',
      status: leaveDays.length === 0
        ? 'No leave days yet'
        : (lowDays.length ? `${LOW_DAYS} days or fewer left` : 'Days look fine'),
      tone: lowDays.length ? 'watch' : 'clear',
    },
  ];

  const shownDays = lowDays
    .filter((item) => item.employee_name.toLowerCase().includes(nameQuery.trim().toLowerCase()))
    .slice()
    .sort((a, b) => Number(a.remaining_days) - Number(b.remaining_days) || a.employee_name.localeCompare(b.employee_name));

  const employeeName = (item) => {
    const name = item.employee_name || '';
    if (!canOpenCard || !item.employee_id) return name;
    return (
      <Link
        to="/employee-card"
        state={{ employee: { employee_id: item.employee_id, employee_name: name } }}
      >
        {name}
      </Link>
    );
  };

  const toggle = (id) => setOpenId((current) => (current === id ? null : id));

  return (
    <div className="leave-page benefits-dashboard">
      <h2>Employee Benefits</h2>
      {data && <p className="benefits-headline">{headline}</p>}
      <p className="benefits-hint">Tap a box to see the names. <Link to="/leave-requests">All leave requests</Link></p>
      {error && <p className="leave-error">{error}</p>}
      {message && <p className="leave-ok">{message}</p>}
      {!data && !error && <p>Loading...</p>}

      {data && (
        <>
          <div className="benefits-grid">
            {cards.map((card) => (
              <button
                key={card.id}
                type="button"
                className={`benefits-card ${card.tone}${openId === card.id ? ' open' : ''}`}
                aria-expanded={openId === card.id}
                onClick={() => toggle(card.id)}
              >
                <span className="benefits-count">{card.count}</span>
                <span className="benefits-title">{card.title}</span>
                <span className="benefits-status">{card.status}</span>
              </button>
            ))}
          </div>

          {openId === 'pay' && (
            <section className="benefits-detail">
              <h3>Allowance due</h3>
              <p className="leave-note">These people are due their leave allowance. The voucher is issued when their leave request is approved.</p>
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Anniversary</th>
                    <th>Used days</th>
                    <th>Remaining days</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {payDue.map((item) => (
                    <tr key={item.balance_id}>
                      <td>{employeeName(item)}</td>
                      <td>{item.leave_year_start}</td>
                      <td>{formatDays(item.used_days)}</td>
                      <td>{formatDays(item.remaining_days)}</td>
                      <td>
                        {money(item.amount)}
                        {item.missing_taxable && <div className="leave-note">Base pay is missing.</div>}
                      </td>
                    </tr>
                  ))}
                  {payDue.length === 0 && (
                    <tr>
                      <td colSpan="5">No allowance is due today.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {openId === 'service' && (
            <section className="benefits-detail">
              <h3>End of service this year</h3>
              <p className="leave-note">This amount is set aside until the person leaves. It is not paid now.</p>
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Anniversary</th>
                    <th>Years</th>
                    <th>Days</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {endOfService.map((item) => (
                    <tr key={item.accrual_id}>
                      <td>{employeeName(item)}</td>
                      <td>{item.anniversary_date}</td>
                      <td>{item.completed_years}</td>
                      <td>{formatDays(item.entitled_days)}</td>
                      <td>
                        {money(item.amount)}
                        {item.missing_taxable && <div className="leave-note">Base pay is missing.</div>}
                      </td>
                    </tr>
                  ))}
                  {endOfService.length === 0 && (
                    <tr>
                      <td colSpan="5">No end of service amount this year.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {openId === 'hr' && (
            <section className="benefits-detail">
              <h3>Waiting for you</h3>
              <p className="leave-note">The manager has already approved these.</p>
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Dates</th>
                    <th>Days</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {waitingForHr.map((request) => (
                    <tr key={request.request_id}>
                      <td>{employeeName(request)}</td>
                      <td>
                        {TYPE_LABELS[request.leave_type] || request.leave_type}
                        {request.prevented && <div className="leave-note">Forfeited Leave Allowance</div>}
                      </td>
                      <td>{request.start_date} to {request.end_date}</td>
                      <td>{request.working_days}</td>
                      <td>
                        <div className="leave-actions">
                          {canHr && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => act(request.request_id, 'hr-approve')}
                            >
                              HR approve
                            </button>
                          )}
                          {canEdit && (
                            <button type="button" className="danger" disabled={busy} onClick={() => act(request.request_id, 'reject')}>
                              Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {waitingForHr.length === 0 && (
                    <tr>
                      <td colSpan="5">Nothing is waiting for you.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {openId === 'out' && (
            <section className="benefits-detail">
              <h3>On leave today</h3>
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Last day</th>
                  </tr>
                </thead>
                <tbody>
                  {onLeave.map((request) => (
                    <tr key={request.request_id}>
                      <td>{employeeName(request)}</td>
                      <td>{TYPE_LABELS[request.leave_type] || request.leave_type}</td>
                      <td>{request.end_date}</td>
                    </tr>
                  ))}
                  {onLeave.length === 0 && (
                    <tr>
                      <td colSpan="3">No one is on leave today.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {openId === 'soon' && (
            <section className="benefits-detail">
              <h3>Coming soon</h3>
              <p className="leave-note">Leave pay for the new year starts on this date.</p>
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Due on</th>
                  </tr>
                </thead>
                <tbody>
                  {comingSoon.map((item) => (
                    <tr key={item.employee_id}>
                      <td>{employeeName(item)}</td>
                      <td>{item.anniversary_date}</td>
                    </tr>
                  ))}
                  {comingSoon.length === 0 && (
                    <tr>
                      <td colSpan="2">No anniversaries in the next 30 days.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {openId === 'manager' && (
            <section className="benefits-detail">
              <h3>With the manager</h3>
              <p className="leave-note">These are waiting for the manager before you can approve them.</p>
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Dates</th>
                  </tr>
                </thead>
                <tbody>
                  {waitingForManager.map((request) => (
                    <tr key={request.request_id}>
                      <td>{employeeName(request)}</td>
                      <td>{TYPE_LABELS[request.leave_type] || request.leave_type}</td>
                      <td>{request.start_date} to {request.end_date}</td>
                    </tr>
                  ))}
                  {waitingForManager.length === 0 && (
                    <tr>
                      <td colSpan="3">Nothing is waiting for a manager.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {openId === 'setup' && (
            <section className="benefits-detail">
              <h3>Needs setup</h3>
              <p className="leave-note">Ask the accountant to set up leave for these people.</p>
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                  </tr>
                </thead>
                <tbody>
                  {needsSetup.map((person) => (
                    <tr key={person.employee_id}>
                      <td>{employeeName(person)}</td>
                    </tr>
                  ))}
                  {needsSetup.length === 0 && (
                    <tr>
                      <td>Everyone in the system has leave set up.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {openId === 'days' && (
            <section className="benefits-detail">
              <h3>Days running low</h3>
              <p className="leave-note">These people have {LOW_DAYS} days or fewer left.</p>
              <input
                className="benefits-search"
                type="search"
                placeholder="Find a name"
                value={nameQuery}
                onChange={(event) => setNameQuery(event.target.value)}
                aria-label="Find a name"
              />
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Used days</th>
                    <th>Remaining days</th>
                  </tr>
                </thead>
                <tbody>
                  {shownDays.map((item) => (
                    <tr key={item.employee_id}>
                      <td>{employeeName(item)}</td>
                      <td>{formatDays(item.used_days)}</td>
                      <td>{formatDays(item.remaining_days)}</td>
                    </tr>
                  ))}
                  {shownDays.length === 0 && (
                    <tr>
                      <td colSpan="3">{lowDays.length === 0 ? `No one has ${LOW_DAYS} days or fewer left.` : 'No matching names.'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}

    </div>
  );
};

export default EmployeeBenefitsDashboard;
